import axios, {
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosInstance,
} from 'axios'
import qs from 'qs'
import { message } from 'antd'

export type TEnv = 'dev' | 'beta' | string

type TRequest = <R = any, Q = any>(
  config: AxiosRequestConfig<Q> & {
    query?: any
  }
) => Promise<TResponse<R>>

export type TResponse<T = any> = {
  data: T
  code: number
  msg: string
  $response: AxiosResponse
  [x: string]: any
}

export const ENV_CACHE = 'env_cache'
export const initialEnv = (): TEnv => {
  const { hostname } = window.location
  if (/micun\.com/.test(hostname)) {
    return ''
  }

  const cache = localStorage.getItem(ENV_CACHE)

  if (/localhost/.test(hostname)) {
    return cache || 'dev'
  }

  if (/dev/.test(hostname)) {
    return cache || 'dev'
  }

  if (/beta/.test(hostname)) {
    return cache || 'beta'
  }

  return ''
}

export const setEnv = (nextEnv: TEnv) => {
  env = nextEnv
  localStorage.setItem(ENV_CACHE, nextEnv)
}

export let env = initialEnv()

export const isAbsolute = (url: string) => /^http/.test(url)

export const transformUrl = (
  env: TEnv,
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  // return config
  const { url } = config
  const abs = isAbsolute(url || '')

  if (abs) {
    return config
  }

  const isSSO = /^\/?sso/.test(url || '')

  if (env) {
    if (isSSO) {
      config.url = `//sso.${env}.micun.cn${url}`
    } else {
      config.url = `//api.${env}.micun.cn${url}`
    }
  } else {
    if (isSSO) {
      config.url = `//sso.micun.com${url}`
    } else {
      config.url = `//api.micun.com${url}`
    }
  }

  return config
}

export const instance = axios.create({
  paramsSerializer: (params) => {
    return qs.stringify(params, {
      allowDots: true,
      arrayFormat: 'indices',
    })
  },
})

instance.interceptors.request.use((config) =>
  transformUrl ? transformUrl(env, config) : config
)

instance.interceptors.response.use(
  (response) => {
    // console.log('fetch response', response)
    if (response.data != null && typeof response.data === 'object') {
      response.data.$response = response
    }
    return response.data
  },
  (error) => {
    // console.log('fetch error', error)
    let response = {}
    if (error.response) {
      response = {
        data: error.response.data,
        code: error.response.status,
        msg: error.response.statusText,
        $response: error,
      }
    } else {
      response = {
        data: error.message,
        code: -9999,
        msg: error.code,
        $response: error,
      }
    }

    return response
  }
)

instance.interceptors.response.use((response: any) => {
  const { code, msg } = response as TResponse
  if (code === 0 || (code >= 200 && code < 300)) {
    return response
  } else {
    message.error(`(${code}) ${msg}`)
  }
  return response
})

const bindMethod =
  (method: string): TRequest =>
  (config) =>
    instance({
      ...config,
      params: config.query,
      method,
    })

export const fetch: TRequest = (config) =>
  instance({
    ...config,
    params: config.query,
  })

export const get = bindMethod('get')
export const post = bindMethod('post')
export const put = bindMethod('put')
export const del = bindMethod('delete')
